import React, { useContext, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedCurrency, FormattedMessage } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_TYPE_FLAT } from '@divvy-web/skylab.basicbutton'
import Slider, { SliderSizes } from '@divvy-web/skylab.slider'
import Spinner from '@divvy-web/skylab.spinner'
import TextLink from '@divvy-web/skylab.textlink'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import { useApolloQueryErrorHandling } from '@sputnik/apollo/useApolloQueryErrorHandling'
import ApplicationAgreements from '@sputnik/components/ApplicationAgreements'
import GoToDashboardButton from '@sputnik/components/GoToDashboardButton'
import ImageWrapper from '@sputnik/components/ImageWrapper'
import PageViewWrapper from '@sputnik/components/PageViewWrapper/PageViewWrapper'
import TilaErrorMessage from '@sputnik/components/TilaErrorMessage'
import TipCard from '@sputnik/components/TipCard'
import { getIsDSAWebBank } from '@sputnik/components/utils'
import useDeviceDetect from '@sputnik/hooks/useDeviceDetect'
import { webBankTipCardMessage } from '@sputnik/pages/ApplicationProcessed/applicationProcessedUtils'
import { DecisionContext } from '@sputnik/pages/ApplicationProcessed/DecisionsContext'
import { useGetCreditApplicationTilaInfo } from '@sputnik/pages/gql/GetCreditApplicationTilaInfo.gql'
import { useSelectedDecision } from '@sputnik/pages/gql/mutations/CreditLineOfferSelectedDecision.gql'
import { PAGE_NAME_STRINGS, PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import styles from './styles.module.scss'

const StandardCreditLineOfferWithSelectorAcceptance = () => {
  const navigate = useNavigate()
  const { appId } = useParams()
  const { pathname } = useLocation()
  const { isMobile } = useDeviceDetect()
  const [getClassName, makeTestId] = useNameFormatter('StandardCreditLineOfferWithSelectorAcceptance')
  const showToast = useToast()
  const { standardLineOffer, isLoggedInUserAuthorizedSigner, authSignerEmail } = useContext(DecisionContext)
  const { approvedAmountInCents, maxApprovedAmountInCents, id: decisionId, offerType } = standardLineOffer || {}
  const {
    data,
    loading: isLoadingTilaInfo,
    error: tilaInfoError,
  } = useGetCreditApplicationTilaInfo({
    fetchPolicy: 'network-only',
    variables: {
      creditApplicationId: appId as string,
      underwritingDecisionUuid: decisionId,
      underwritingOfferType: offerType,
      skipTila: !isLoggedInUserAuthorizedSigner,
    },
    skip: !standardLineOffer || !isLoggedInUserAuthorizedSigner || !appId,
  })

  useApolloQueryErrorHandling({
    error: tilaInfoError,
    toast: {
      message: (
        <FormattedMessage
          defaultMessage='There was an error retrieving your TILA information. Refresh the browser in a few minutes.'
          id='sputnik.StandardCreditLineOfferWithSelectorAcceptance__R0xwTP'
        />
      ),
      options: { autoHideDelay: 5000 },
    },
    logging: {
      eventName: 'StandardCreditLineOfferWithSelectorAcceptance',
      message: 'Error fetching TILA information for standard credit line offer acceptance',
      action: 'getCreditApplicationTilaInfo',
      attributes: {
        creditAppId: appId,
      },
    },
  })

  const [selectedDecision, { loading: isSelectedDecisionLoading }] = useSelectedDecision({
    onCompleted: () => {
      logInfo('selectedDecision', {
        attributes: {
          creditAppId: appId,
          result: `accepts offer for secured credit line and send to polling (/app/${appId}/section/${PATHNAME_CONSTS.ACCEPTING_OFFER})`,
        },
        eventName: 'StandardCreditLineOfferWithSelectorAcceptance',
      })

      navigate({ pathname: `/app/${appId}/section/${PATHNAME_CONSTS.ACCEPTING_OFFER}` })
    },
    onError: (e) => {
      logError(e, {
        attributes: {
          action: 'selectedDecision',
          creditAppId: appId,
          message: e?.message,
          result: 'Error accepting offer for standard credit line',
        },
        eventName: 'StandardCreditLineOfferWithSelectorAcceptance',
      })

      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Failed to load business address data'
          id='sputnik.BusinessAddressForm__ArGQET'
        />,
      )
    },
  })

  const isSingleOffer = !pathname.includes('credit-line-offers')

  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false)
  const [sliderSelectedValue, setSliderSelectedValue] = useState(approvedAmountInCents / 100)
  const creditApplication = data?.creditApplication
  const tila: { content?: string; meta?: { signedAt?: string } } = data?.creditApplication?.tila || {}
  const dsaVersion = data?.creditApplication?.dsaVersion
  const isLoading = isLoadingTilaInfo || isSelectedDecisionLoading
  const hasTilaContent = !!tila?.content
  const hasAgreedToTila = !!tila?.meta?.signedAt
  const disabledWithTila = !hasAgreedToTerms || isLoading || (hasTilaContent && !hasAgreedToTila) || !creditApplication
  const disabledWithoutTila = !hasAgreedToTerms || isLoading
  const disableAcceptOfferButton = hasTilaContent ? disabledWithTila : disabledWithoutTila
  const shouldShowWebBankTip = getIsDSAWebBank(dsaVersion)

  const handleCheckboxClick = () => {
    setHasAgreedToTerms(!hasAgreedToTerms)
  }

  const handleAcceptClick = () => {
    selectedDecision({
      variables: {
        id: decisionId,
        salesforceCreditId: appId as string,
        selectedAmount: sliderSelectedValue * 100,
      },
    })
  }

  if (isLoadingTilaInfo || !standardLineOffer) {
    return (
      <div className={styles[getClassName('')]}>
        <Spinner centered />
      </div>
    )
  }

  return (
    <PageViewWrapper pageName={PAGE_NAME_STRINGS.PAGE_STANDARD_OFFER}>
      <div className={styles[getClassName('')]}>
        <div className={styles[getClassName('main-container')]}>
          <div className={styles[getClassName('page-content')]}>
            <div className={styles[getClassName('body')]}>
              <div className={styles[getClassName('title')]}>
                <FormattedMessage
                  defaultMessage='Standard credit line offer'
                  id='sputnik.StandardCreditLineOfferWithSelectorAcceptance__iqLKGL'
                />
              </div>
              {isMobile && (
                <div>
                  <ImageWrapper
                    alt='person scanning mobile device'
                    imageName={'credit-line-offer-mobile'}
                  />
                </div>
              )}
              <div className={styles[getClassName('subtitle')]}>
                <FormattedMessage
                  defaultMessage='Great news—you’re approved for a maximum of {maxCredit}
                    which is higher than the {desiredCredit} you requested. Just use the slider 
                    to choose the exact amount you need.'
                  id='sputnik.StandardCreditLineOfferWithSelectorAcceptance__BSHMT4'
                  values={{
                    desiredCredit: (
                      <FormattedCurrency
                        maximumFractionDigits={0}
                        minimumFractionDigits={2}
                        value={approvedAmountInCents}
                      />
                    ),
                    maxCredit: (
                      <FormattedCurrency
                        maximumFractionDigits={0}
                        minimumFractionDigits={2}
                        value={maxApprovedAmountInCents}
                      />
                    ),
                  }}
                />
              </div>
              <div
                className={styles[getClassName('credit-selector')]}
                data-testid={makeTestId('credit-selector')}
              >
                <div
                  className={styles[getClassName('credit-selected-value')]}
                  data-testid={makeTestId('credit-selected-value')}
                >
                  <FormattedMessage
                    defaultMessage='Selected amount: {sliderSelectedValue}'
                    id='sputnik.StandardCreditLineOfferWithSelectorAcceptance__oRIVFo'
                    values={{
                      sliderSelectedValue: (
                        <FormattedCurrency
                          maximumFractionDigits={0}
                          minimumFractionDigits={0}
                          value={sliderSelectedValue}
                        />
                      ),
                    }}
                  />
                </div>
                <div
                  className={styles[getClassName('slider-container')]}
                  data-testid={makeTestId('slider-container')}
                >
                  <div
                    className={styles[getClassName('slider-min')]}
                    data-testid={makeTestId('slider-min')}
                  >
                    <FormattedMessage
                      defaultMessage='{desiredCredit}k'
                      id='sputnik.StandardCreditLineOfferWithSelectorAcceptance__1BIVlB'
                      values={{
                        desiredCredit: (
                          <FormattedCurrency
                            maximumFractionDigits={0}
                            minimumFractionDigits={5}
                            value={approvedAmountInCents}
                          />
                        ),
                      }}
                    />
                  </div>
                  <div
                    className={styles[getClassName('slider')]}
                    data-testid={makeTestId('slider')}
                  >
                    <Slider
                      label=''
                      max={maxApprovedAmountInCents / 100}
                      min={approvedAmountInCents / 100}
                      size={SliderSizes.SizeLarge}
                      step={1000}
                      value={[sliderSelectedValue]}
                      onChange={(value) => setSliderSelectedValue(Array.isArray(value) ? value[0] : value)}
                    />
                  </div>
                  <div
                    className={styles[getClassName('slider-max')]}
                    data-testid={makeTestId('slider-max')}
                  >
                    <FormattedMessage
                      defaultMessage='{maxApprovedAmount}k'
                      id='sputnik.StandardCreditLineOfferWithSelectorAcceptance__Qrro1B'
                      values={{
                        maxApprovedAmount: (
                          <FormattedCurrency
                            maximumFractionDigits={0}
                            minimumFractionDigits={5}
                            value={maxApprovedAmountInCents}
                          />
                        ),
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className={styles[getClassName('how-it-works')]}>
                <FormattedMessage
                  defaultMessage='How the credit line works:'
                  id='sputnik.StandardCreditLineOfferWithSelectorAcceptance__UZgb4A'
                />
              </div>
              <div className={styles[getClassName('how-it-works-list')]}>
                <ul>
                  <li>
                    <FormattedMessage
                      defaultMessage="Your credit limit is fixed and typically won't change."
                      id='sputnik.StandardCreditLineOfferWithSelectorAcceptance__kT0CVZ'
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      defaultMessage='You can request credit limit increases right from your dashboard.'
                      id='sputnik.StandardCreditLineOfferWithSelectorAcceptance__BwrSOE'
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      defaultMessage='Your balance is due in full at the end of each billing cycle.'
                      id='sputnik.StandardCreditLineOfferWithSelectorAcceptance__8LBAFn'
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      defaultMessage='Spend at least 30% of your credit line each month to earn rewards.'
                      id='sputnik.StandardCreditLineOfferWithSelectorAcceptance__B8LOfl'
                    />
                  </li>
                </ul>
              </div>
              {isMobile && shouldShowWebBankTip && (
                <div className={styles[getClassName('web-bank-tip-card')]}>
                  <TipCard bodyText={webBankTipCardMessage} />
                </div>
              )}
              <p className={styles[getClassName('learn-more-wrapper')]}>
                <FormattedMessage
                  defaultMessage='Still need more info? <textlink>Learn more</textlink>'
                  id='sputnik.StandardCreditLineOfferWithSelectorAcceptance__VjFJsV'
                  values={{
                    textlink: (chunks) => (
                      <TextLink
                        className={getClassName('learn-more')}
                        href='https://help.bill.com/direct/s/article/000003577'
                        target='_blank'
                      >
                        {chunks}
                      </TextLink>
                    ),
                  }}
                />
              </p>
              {tilaInfoError && <TilaErrorMessage />}
              {isLoggedInUserAuthorizedSigner && offerType && creditApplication && !tilaInfoError && (
                <ApplicationAgreements
                  creditApplication={creditApplication}
                  decisionId={decisionId}
                  hasAgreedToOfferSummary={hasAgreedToTila}
                  hasAgreedToTerms={hasAgreedToTerms}
                  offerType={offerType}
                  onCheckboxClick={handleCheckboxClick}
                />
              )}
              {!isLoggedInUserAuthorizedSigner && !tilaInfoError && (
                <div className={styles[getClassName('unauthorized-accept-message')]}>
                  <FormattedMessage
                    defaultMessage='Only the authorized signer ({email}) can accept this offer.'
                    id='sputnik.StandardCreditLineOfferWithSelectorAcceptance__TdYnmo'
                    values={{
                      email: authSignerEmail,
                    }}
                  />
                </div>
              )}
              <section className={styles[getClassName('buttons')]}>
                {!isSingleOffer && (
                  <BasicButton
                    className={styles[getClassName('previous')]}
                    dataTestId={makeTestId('previous')}
                    type={BASIC_BUTTON_TYPE_FLAT}
                    onClick={() => navigate(-1)}
                  >
                    <FormattedMessage
                      defaultMessage='Previous'
                      id='sputnik.StandardCreditLineOfferWithSelectorAcceptance__JJNc3c'
                    />
                  </BasicButton>
                )}
                {isSingleOffer && (
                  <GoToDashboardButton
                    buttonType={BASIC_BUTTON_TYPE_FLAT}
                    componentName={makeTestId('')}
                  />
                )}
                {isLoggedInUserAuthorizedSigner && (
                  <BasicButton
                    className={styles[getClassName('accept')]}
                    dataTestId={makeTestId('accept')}
                    disabled={disableAcceptOfferButton}
                    showSpinner={isSelectedDecisionLoading}
                    onClick={handleAcceptClick}
                  >
                    <FormattedMessage
                      defaultMessage='Accept offer'
                      id='sputnik.StandardCreditLineOfferWithSelectorAcceptance__/xro5W'
                    />
                  </BasicButton>
                )}
              </section>
            </div>
          </div>
        </div>
      </div>
    </PageViewWrapper>
  )
}

export default StandardCreditLineOfferWithSelectorAcceptance
